import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { I18nKey } from "../../i18n/i18n";
import { FILTER_CONTROL } from "./FilterControls";

type BadgeFilterOption<T extends string | number> = {
  value: T;
  label: string | I18nKey;
};

type BadgeFilterProps<T extends string | number> = {
  value: T | "";
  onChange: (value: T | "") => void;
  options: BadgeFilterOption<T>[];
  renderBadge: (value: T) => ReactNode;
  emptyLabel?: string;
};

export function BadgeFilter<T extends string | number>({
  value,
  onChange,
  options,
  renderBadge,
  emptyLabel,
}: BadgeFilterProps<T>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedOption = value
    ? options.find((opt) => opt.value === value)
    : null;

  return (
    <div className="hidden md:block relative" ref={dropdownRef}>
      <label className="block text-sm mb-1">{t("filter-by")}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`${FILTER_CONTROL} min-w-[200px] text-left flex items-center justify-between gap-2`}
      >
        <span>
          {selectedOption ? (
            renderBadge(selectedOption.value)
          ) : (
            <span className="text-grey-medium">
              {emptyLabel || t("common-select")}
            </span>
          )}
        </span>
        <FontAwesomeIcon
          icon="chevron-down"
          className={`h-3 w-3 text-grey-medium transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white rounded-lg border border-grey-medium shadow-lg text-sm">
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                onChange("" as T | "");
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 hover:bg-grey-light transition-colors ${
                value === "" ? "bg-grey-light" : ""
              }`}
            >
              <span className="text-grey-medium">
                {emptyLabel || t("common-select")}
              </span>
            </button>
            {options.map((option) => (
              <button
                key={String(option.value)}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 hover:bg-grey-light transition-colors flex items-center ${
                  value === option.value ? "bg-grey-light" : ""
                }`}
              >
                {renderBadge(option.value)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
